import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Button, Input, Message, Segment, Select } from "semantic-ui-react";
import { toast } from "../..";
import { UserData, userInit, UserRole } from "../../actions/authentificationActions";
import { TranslateProps } from "../../i18n/TranslateProps";
import { createUser } from "../../api/users";
import { userAuthorityOptions, userRoleOptions } from "../../store/userReducer";

type Props = {
    users: UserData[];
    setUsers: Dispatch<SetStateAction<UserData[]>>;
};

export const AddUserForm = ({ users, setUsers }: Props) => {
    const intl = useIntl();
    const [newUser, setNewUser] = useState(userInit);
    const [loading, setLoading] = useState(false);
    const [type, setType] = useState("text");
    const [errors, setErrors] = useState<string[]>([]);

    const handleAddNewUser = async () => {
        setLoading(true);
        try {
            const res = await createUser(newUser);
            setUsers(users.concat(res.data as UserData));
            setNewUser(userInit);
            toast(intl.formatMessage({ id: "userAdded" }), true);
        } catch {
            toast(intl.formatMessage({ id: "userAddFail" }), false);
        } finally {
            setLoading(false);
        }
    };

    const handleType = (e: any) => {
        if (e._reactName === "onBlur" && e.target.value === "") setType("text");
        else setType("password");
    };

    useEffect(() => {
        if (newUser.role === UserRole.OfficeUserViadonau) {
            setNewUser(n => ({ ...n, authority: "AT90" }));
        } else if (newUser.authority === "AT90") {
            setNewUser(n => ({ ...n, authority: "AT01" }));
        }
    }, [newUser.role, newUser.authority]);

    useEffect(() => {
        if (
            newUser.username.length > 0 &&
            (!newUser.username.includes("@") || !newUser.username.includes("."))
        )
            setErrors(x => (!x.some(e => e === "emailInvalid") ? x.concat("emailInvalid") : x));
        else setErrors(x => x.filter(e => e !== "emailInvalid"));
    }, [newUser.username]);

    useEffect(() => {
        if (newUser.password.length > 0) {
            if (newUser.password.length < 8)
                setErrors(x =>
                    !x.some(e => e === "passwordTooShort") ? x.concat("passwordTooShort") : x
                );
            else setErrors(x => x.filter(e => e !== "passwordTooShort"));
            if (!/\d/.test(newUser.password))
                setErrors(x =>
                    !x.some(e => e === "passwordNoNumber") ? x.concat("passwordNoNumber") : x
                );
            else setErrors(x => x.filter(e => e !== "passwordNoNumber"));
            if (!/[ `!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(newUser.password))
                setErrors(x =>
                    !x.some(e => e === "passwordNoSpecialCharacter")
                        ? x.concat("passwordNoSpecialCharacter")
                        : x
                );
            else setErrors(x => x.filter(e => e !== "passwordNoSpecialCharacter"));
        }
    }, [newUser.password]);

    return (
        <div className="add-user-form-container">
            <h1>{intl.formatMessage({ id: "addUser" })}</h1>
            <Segment className="grey-segment">
                <form className="add-user-form" autoComplete="off">
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "email" })}</label>
                        <Input
                            type="text"
                            value={newUser.username}
                            onChange={(e: any) =>
                                setNewUser({ ...newUser, username: e.target.value })
                            }
                            autoComplete="new-password"
                        />
                    </section>
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "password" })}</label>
                        <Input
                            type={type}
                            value={newUser.password}
                            onChange={(e: any) =>
                                setNewUser({ ...newUser, password: e.target.value })
                            }
                            autoComplete="new-password"
                            onFocus={handleType}
                            onBlur={handleType}
                        />
                    </section>
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "firstName" })}</label>
                        <Input
                            value={newUser.firstName}
                            onChange={(e: any) =>
                                setNewUser({ ...newUser, firstName: e.target.value })
                            }
                            autoComplete="new-password"
                        />
                    </section>
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "lastName" })}</label>
                        <Input
                            value={newUser.lastName}
                            onChange={(e: any) =>
                                setNewUser({ ...newUser, lastName: e.target.value })
                            }
                            autoComplete="new-password"
                        />
                    </section>
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "userRole" })}</label>
                        <Select
                            selectOnBlur={false}
                            options={TranslateProps(userRoleOptions)}
                            value={newUser.role}
                            onChange={(e, data: any) =>
                                setNewUser({ ...newUser, role: data.value })
                            }
                        />
                    </section>
                    <section className="input-container">
                        <label>{intl.formatMessage({ id: "authority" })}</label>
                        <Select
                            selectOnBlur={false}
                            options={userAuthorityOptions}
                            disabled={newUser.role === UserRole.OfficeUserViadonau}
                            value={newUser.authority}
                            onChange={(e, data: any) =>
                                setNewUser({ ...newUser, authority: data.value })
                            }
                        />
                    </section>
                </form>
                {errors.length > 0 && (
                    <Message
                        className="error-message"
                        icon="exclamation circle"
                        content={errors.map(e => (
                            <span key={e}>{intl.formatMessage({ id: e })}</span>
                        ))}
                    />
                )}
                <Button
                    content={intl.formatMessage({ id: "addNewUser" })}
                    icon="add user"
                    disabled={
                        loading || errors.length > 0 || Object.values(newUser).some(x => x === "")
                    }
                    loading={loading}
                    primary
                    fluid
                    onClick={handleAddNewUser}
                />
            </Segment>
        </div>
    );
};
